import React from 'react';
import Button from '@material-ui/core/Button';

import {
  ExpandableSettingsGroupGrid,
  ValueBox
} from '../FormWidgets/SettingGroup';
import IrrigationEvent from '../FormWidgets/IrrigationEvent';

export const GCSPI_IRRIGATION_TIME_PHASE_P1 = 1;
export const GCSPI_IRRIGATION_TIME_PHASE_P2 = 2;
export const GCSPI_IRRIGATION_MAX_EVENTS = 64;
export const P1_EVENT_START_MAX_MINUTES = 4 * 60;

export function durationSecondsToRoundedMinutes(durationSeconds) {
  if (durationSeconds <= 0) return 0;
  return Math.ceil(durationSeconds / 60);
}

export function isP1EventStartOutOfBounds(event) {
  if (!event || Number(event.phase) !== GCSPI_IRRIGATION_TIME_PHASE_P1) return false;

  const startMinute = Number(event.start_minute_from_phase_begin);
  return !Number.isFinite(startMinute) || startMinute < 0 || startMinute > P1_EVENT_START_MAX_MINUTES;
}

function getMinutesDifference(endMinutes, startMinutes) {
  if (endMinutes >= startMinutes) return endMinutes - startMinutes;
  return 24 * 60 - startMinutes + endMinutes;
}

function formatClockMinutes(totalMinutes) {
  const minutesInDay = 24 * 60;
  const wrappedMinutes =
    ((Math.round(totalMinutes) % minutesInDay) + minutesInDay) % minutesInDay;
  const hours = Math.floor(wrappedMinutes / 60);
  const minutes = wrappedMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export default function IrrigationEventsEditor({
  t,
  controller,
  controls,
  events = [],
  controllerEvents = [],
  irrigationSettings = [],
  irrigationIndex,
  phase,
  valuePath = 'irrigation_events',
  expandId,
  dayStartMinutes,
  dayEndMinutes,
  writeAccess = true,
  marker,
  mobile = false
}) {
  controls.retainSetting?.(valuePath);
  const irrigationId = Number(irrigationIndex);
  const matchingEvents = events
    .map((evt, i) => ({ evt, i }))
    .filter(({ evt }) => evt && Number(evt.irrigation_index) === irrigationId && Number(evt.phase) === phase);

  const getP1LengthMinutes = () => {
    let latestEndMinute = 0;
    events.forEach((event) => {
      if (
        event &&
        Number(event.irrigation_index) === irrigationId &&
        Number(event.phase) === GCSPI_IRRIGATION_TIME_PHASE_P1 &&
        event.enabled
      ) {
        latestEndMinute = Math.max(
          latestEndMinute,
          Number(event.start_minute_from_phase_begin || 0) +
            durationSecondsToRoundedMinutes(event.duration_seconds)
        );
      }
    });
    return latestEndMinute;
  };

  const getPreviousEventEndMinute = (arr, index) => {
    if (index <= 0) return 0;
    const previous = arr[index - 1].evt;
    return (
      Number(previous.start_minute_from_phase_begin || 0) +
      durationSecondsToRoundedMinutes(previous.duration_seconds)
    );
  };

  const hasOverlap = (event, arr, index) => {
    if (!event.enabled) return false;
    const previous = [...arr.slice(0, index)].reverse().find(({ evt }) => evt.enabled)?.evt;
    const next = arr.slice(index + 1).find(({ evt }) => evt.enabled)?.evt;
    const start = Number(event.start_minute_from_phase_begin || 0);
    const end = start + durationSecondsToRoundedMinutes(event.duration_seconds);

    return Boolean(
      (previous &&
        start <
          Number(previous.start_minute_from_phase_begin || 0) +
            durationSecondsToRoundedMinutes(previous.duration_seconds)) ||
        (next && end > Number(next.start_minute_from_phase_begin || 0))
    );
  };

  const getP2AllowedStartRange = () => {
    const irrigation = irrigationSettings[irrigationId] || {};
    const endBeforeSunset = irrigation.irrigation_p2_end_before_sunset;
    let endBeforeSunsetMinutes = Number(endBeforeSunset);
    if (typeof endBeforeSunset === 'string') {
      const [hour = 0, minute = 0] = endBeforeSunset.split(':').map(Number);
      endBeforeSunsetMinutes = hour * 60 + minute;
    }
    const dayLength = getMinutesDifference(dayEndMinutes, dayStartMinutes);
    const beforeSunset = getMinutesDifference(dayEndMinutes, endBeforeSunsetMinutes);
    const p0Length = irrigation.irrigation_p0_enable
      ? Number(irrigation.irrigation_p0_phase_timeout_minutes) || 0
      : 0;
    return Math.max(0, dayLength - p0Length - getP1LengthMinutes() - beforeSunset);
  };

  const getEventClockTime = (event) => {
    const irrigation = irrigationSettings[irrigationId] || {};
    const p0Length = irrigation.irrigation_p0_enable
      ? Number(irrigation.irrigation_p0_phase_timeout_minutes) || 0
      : 0;
    const p1Start = dayStartMinutes + p0Length;
    const phaseStart =
      Number(event.phase) === GCSPI_IRRIGATION_TIME_PHASE_P2
        ? p1Start + getP1LengthMinutes()
        : p1Start;
    return formatClockMinutes(phaseStart + Number(event.start_minute_from_phase_begin || 0));
  };

  const setEvents = (updatedEvents) => controls.setValue(valuePath, updatedEvents);
  const removeEvent = (eventIndex) =>
    setEvents(events.filter((_, currentIndex) => currentIndex !== eventIndex));
  const addEvent = () => {
    if (events.length >= GCSPI_IRRIGATION_MAX_EVENTS) return;
    const previous = matchingEvents[matchingEvents.length - 1]?.evt;
    const startOffset = phase === GCSPI_IRRIGATION_TIME_PHASE_P1 ? 10 : 60;
    const firstStart = phase === GCSPI_IRRIGATION_TIME_PHASE_P2 ? 60 : 0;
    setEvents([
      ...events,
      {
        irrigation_index: irrigationId,
        phase,
        start_minute_from_phase_begin: previous
          ? Number(previous.start_minute_from_phase_begin || 0) + startOffset
          : firstStart,
        duration_seconds: previous ? previous.duration_seconds : 180,
        enabled: true
      }
    ]);
  };

  const p2Max = phase === GCSPI_IRRIGATION_TIME_PHASE_P2 ? getP2AllowedStartRange() : null;

  return (
    <ExpandableSettingsGroupGrid
      expand_id={expandId}
      label={`${t('irrigation_events')} (${matchingEvents.length})`}
      outside={true}
    >
      {matchingEvents.map(({ evt, i }, index, arr) => {
        const previousEnd = getPreviousEventEndMinute(arr, index);
        const start = Number(evt.start_minute_from_phase_begin || 0);
        const end = start + durationSecondsToRoundedMinutes(evt.duration_seconds);
        const outOfBounds =
          phase === GCSPI_IRRIGATION_TIME_PHASE_P1
            ? isP1EventStartOutOfBounds(evt)
            : start > p2Max || end > p2Max;

        return (
          <IrrigationEvent
            key={`${i}-${irrigationId}-${phase}`}
            event={evt}
            marker={marker}
            savedEvent={controllerEvents[i]}
            index={i}
            number={index + 1}
            valuePath={valuePath}
            getClockTime={(startMinute) =>
              getEventClockTime({ ...evt, start_minute_from_phase_begin: startMinute })
            }
            minStart={previousEnd}
            maxStart={phase === GCSPI_IRRIGATION_TIME_PHASE_P1 ? P1_EVENT_START_MAX_MINUTES : p2Max}
            startError={hasOverlap(evt, arr, index) || start < previousEnd || outOfBounds}
            durationError={hasOverlap(evt, arr, index) || outOfBounds}
            controller={controller}
            controls={controls}
            writeAccess={writeAccess}
            onDelete={removeEvent}
          />
        );
      })}

      <ValueBox>
        <Button
          onClick={addEvent}
          disabled={!writeAccess || events.length >= GCSPI_IRRIGATION_MAX_EVENTS}
        >
          {t('add_irrigation_event')}
        </Button>
      </ValueBox>
    </ExpandableSettingsGroupGrid>
  );
}
